import React, { useState, useEffect, useContext, useCallback } from "react";
import { getFamilies, createFamily, updateFamily, deleteFamily } from "../api";
import { AuthContext } from "../AuthContext";
import { Pencil, Trash2 } from "lucide-react";

export default function Families() {
  const { user } = useContext(AuthContext);

  const [families, setFamilies] = useState([]);
  const [familia, setFamilia] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [msg, setMsg] = useState(null);

  // 🔹 Buscar famílias
  const fetchFamilies = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await getFamilies(user.token);
      setFamilies(data);
    } catch (error) {
      setMsg({ type: "error", text: error.message });
    } finally {
      setLoadingList(false);
    }
  }, [user.token]);

  useEffect(() => {
    fetchFamilies();
  }, [fetchFamilies]);

  // 🔹 Criar / Atualizar
  async function handleSubmit(e) {
    e.preventDefault();
    setMsg(null);

    if (!familia.trim()) {
      setMsg({ type: "error", text: "Nome da família é obrigatório." });
      return;
    }

    setLoading(true);

    try {
      if (editingId) {
        await updateFamily(editingId, user.token, familia);
        setMsg({ type: "success", text: "Família atualizada com sucesso!" });
      } else {
        await createFamily(user.token, familia);
        setMsg({ type: "success", text: "Família cadastrada com sucesso!" });
      }

      setFamilia("");
      setEditingId(null);
      fetchFamilies(); // 🔥 atualiza lista
    } catch (error) {
      setMsg({ type: "error", text: error.message });
    } finally {
      setLoading(false);
    }
  }

  // 🔹 Editar
  function handleEdit(family) {
    setFamilia(family.familia);
    setEditingId(family.id);
    setMsg(null);
  }

  function handleCancelEdit() {
    setFamilia("");
    setEditingId(null);
  }

  // 🔹 Deletar
  async function handleDelete(id) {
    if (!window.confirm("Deseja excluir esta família?")) return;

    try {
      await deleteFamily(id, user.token);
      setMsg({ type: "success", text: "Família excluída com sucesso!" });
      setFamilies(prev => prev.filter(f => f.id !== id));
    } catch (error) {
      setMsg({ type: "error", text: error.message });
    }
  }

  return (
    <div className="page-container">
      <div className="dashboard-container">
        <div className="user-card" style={{ maxWidth: "900px", margin: "0 auto" }}>

          {/* Cabeçalho */}
          <div style={{ 
            justifyContent: "space-between", 
            alignItems: "center", 
            marginBottom: "2rem" 
          }}>
            <h3>{editingId ? "Editar Família" : "Nova Família"}</h3>
          </div>

          {/* ==================== FORMULÁRIO ==================== */}
          <div className="item-row add-row" style={{ marginBottom: "2.5rem", gridTemplateColumns: "1fr 1fr auto"}}>
            <div className="item-input" style={{ gridColumn: "span 2" }}>
              <label>Nome da Família</label>
              <input
                value={familia}
                onChange={(e) => setFamilia(e.target.value)}
                placeholder="Ex: Eletrônicos, Vestuário, Alimentos..."
                className="input-text"
              />
            </div>

            <div className="item-actions">
              <button 
                type="button"
                className="btn-small"
                onClick={handleSubmit}
                disabled={loading || !familia.trim()}
              >
                {loading ? (
                  <>
                    <span className="spinner" style={{ marginRight: "8px" }}></span>
                    Salvando...
                  </>
                ) : editingId ? (
                  "Salvar Alterações"
                ) : (
                  "Cadastrar Família"
                )}
              </button>
              {editingId && (
                <button 
                  type="button" 
                  className="btn-small"
                  onClick={handleCancelEdit}
                >
                  Cancelar Edição
                </button>
              )}
            </div>
          </div>

          {/* Mensagem de feedback */}
          {msg && (
            <div 
              className={msg.type === "error" ? "error-msg" : "success-msg"} 
              style={{ marginBottom: "2rem" }}
            >
              {msg.text}
            </div>
          )}

          {/* ==================== LISTA DE FAMÍLIAS ==================== */}
          <div style={{ marginTop: "2rem" }}>
            <h3 style={{ marginBottom: "1.5rem" }}>Lista de Famílias</h3>

            {loadingList ? (
              <div className="center-content" style={{ padding: "3rem 0" }}>
                <div className="spinner"></div>
                <p>Carregando famílias...</p>
              </div>
            ) : (
              <div className="items-list">
                {families.length === 0 ? (
                  <p style={{ 
                    opacity: 0.7, 
                    textAlign: "center", 
                    padding: "3rem 0",
                    fontSize: "1.05rem"
                  }}>
                    Nenhuma família cadastrada ainda.
                  </p>
                ) : (
                  families
                    .sort((a, b) => b.id - a.id)
                    .map((f) => (
                      <div 
                        key={f.id}
                        className="item-row"
                        style={{
                          background: editingId === f.id ? "rgba(59, 130, 246, 0.12)" : undefined,
                          border: editingId === f.id ? "1px solid #3b82f6" : undefined,
                          gridTemplateColumns: "1fr auto"
                        }}
                      >
                        <div className="item-input">
                          <label>Família</label>
                          <input value={f.familia} disabled />
                        </div>

                        <div className="item-actions">
                          <button
                            className="btn-small"
                            onClick={() => handleEdit(f)}
                            title="Editar Família"
                          >
                            <Pencil size={18} />
                          </button>

                          <button
                            className="btn-danger btn-small"
                            onClick={() => handleDelete(f.id)}
                            title="Excluir Família"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))
                )}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}